import { useMutation, useQueryClient } from '@tanstack/react-query'

import { postRequest, putRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { UpdateUserPhotoData } from 'src/shared/features/users/schemas/user'

//TODO: decouple image saving logic from user specific update
export const useUploadAndSaveUserImage = () => {
  const queryClient = useQueryClient()
  const { data: user } = useMyUserData()

  return useMutation({
    mutationFn: (params: { uri: string; photoType: 'profile_photo' | 'cover_photo' }) =>
      uploadAndSaveImage(params.uri, params.photoType),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries(queries.users.detailById(user!.id)._ctx.photos(params.photoType))
    },
  })
}

const generatePresignedUrl = async (fileType: string, photoType: string) => {
  const url = `/users/generate-presigned-url?file_type=${encodeURIComponent(fileType)}&photo_type=${encodeURIComponent(
    photoType,
  )}`
  return postRequest<{ key: string; url: string }>(url, {})
}

const updateUserPhoto = (photoData: UpdateUserPhotoData) =>
  putRequest<UpdateUserPhotoData>(`/users/me/photo`, photoData)

const uploadAndSaveImage = async (uri: string, photoType: 'profile_photo' | 'cover_photo') => {
  const imageType = getFileType(uri)
  try {
    const presignedUrlResponse = await generatePresignedUrl(imageType, photoType)
    const url = presignedUrlResponse?.url

    if (!url) throw new Error('Error fetching presigned URL')

    const imageData = await fetch(uri)
    const blob = await imageData.blob()
    const uploadResponse = await fetch(url, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': imageType },
    })

    if (!uploadResponse.ok) throw new Error('Error uploading image')

    const updateData: UpdateUserPhotoData = {
      key: presignedUrlResponse?.key,
      photoType,
    }
    await updateUserPhoto(updateData)
  } catch (error) {
    console.error('Error uploading and saving image:', error)
    throw error
  }
}

function getFileType(uri: string): string {
  if (uri.startsWith('data:')) {
    const mimeType = uri.split(';')[0].split(':')[1]
    return mimeType || 'Unknown'
  }
  switch (uri) {
    case 'image/jpeg':
      return 'image/jpeg'
    case 'image/png':
      return 'image/png'
    case 'image/gif':
      return 'image/gif'
    default:
      return 'Unknown'
  }
}
